/**
 * Web Vitals tracking — reports LCP, INP, CLS, FCP, TTFB to Sentry.
 *
 * Called once from main.tsx after Sentry is initialised.
 * Metrics are sent as Sentry performance spans so they appear in the
 * Performance tab alongside existing transaction traces.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { Sentry } from './sentry';

// Performance budget thresholds (Google "Good" ratings)
const THRESHOLDS: Record<string, { good: number; needsImprovement: number }> = {
  LCP:  { good: 2500,  needsImprovement: 4000  },
  INP:  { good: 200,   needsImprovement: 500   },
  CLS:  { good: 0.1,   needsImprovement: 0.25  },
  FCP:  { good: 1800,  needsImprovement: 3000  },
  TTFB: { good: 800,   needsImprovement: 1800  },
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const t = THRESHOLDS[name];
  if (!t) return 'good';
  if (value <= t.good) return 'good';
  if (value <= t.needsImprovement) return 'needs-improvement';
  return 'poor';
}

function reportMetric(metric: Metric) {
  const rating = getRating(metric.name, metric.value);

  // Always report to Sentry as a measurement
  Sentry.addBreadcrumb({
    category: 'web-vitals',
    message: `${metric.name}: ${Math.round(metric.value)} (${rating})`,
    level: rating === 'poor' ? 'warning' : 'info',
    data: {
      name: metric.name,
      value: metric.value,
      rating,
      id: metric.id,
    },
  });

  // Capture poor vitals as Sentry events so they show up as issues
  if (rating === 'poor') {
    Sentry.captureMessage(`Poor Web Vital: ${metric.name} = ${Math.round(metric.value)}`, {
      level: 'warning',
      tags: {
        vital: metric.name,
        rating,
      },
      extra: {
        value: metric.value,
        id: metric.id,
        navigationType: metric.navigationType,
      },
    });
  }
}

export function initWebVitals() {
  // Only track in production (same guard as Sentry)
  if (!import.meta.env.PROD) return;

  onLCP(reportMetric);
  onINP(reportMetric);
  onCLS(reportMetric);
  onFCP(reportMetric);
  onTTFB(reportMetric);
}
